import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./UserManagement.css";
import { fetchAuthList, fetchRoles } from "../../services/authService";
import AddUserModal from "./AddUserModal";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import { useTheme } from "../../contexts/ThemeContext";
import { TablerSearch } from "../shared/Icons";
import { ROLES } from "../../utils/helpers";
import { getItem, logout, isBootstrapAdmin, getTenantId } from "../../utils/authStorage";
import settingIcon from "../../assets/devices/icons/setting.svg";
import LoadingSpinner from "../shared/LoadingSpinner";
import CustomSelect from "../shared/CustomSelect";
import { ROLE_OPTIONS } from "../shared/selectOptions";
import { toast } from "react-toastify";


const getAvatarClass = (username) => {
  const firstChar = username?.charAt(0).toLowerCase() || 'a';
  if (firstChar >= 'a' && firstChar <= 'h') return 'avatar-blue';
  if (firstChar >= 'i' && firstChar <= 'p') return 'avatar-green';
  return 'avatar-orange';
};

function UserManagement() {
  const { t } = useTranslation();
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [collapsed, setCollapsed] = useState(false);
  const [roles, setRoles] = useState({});

  const usersPerPage = 10;
  const username = getItem("username");
  const role = (getItem("role") || "operator").toLowerCase();
  const { isDarkMode } = useTheme();

  const handleSignOut = () => {
    logout();
  };

  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || '').toLowerCase();
    return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
  };

  useEffect(() => {
    document.body.classList.toggle("modal-open", showModal);
    return () => document.body.classList.remove("modal-open");
  }, [showModal]);

  const fetchUserList = async () => {
    try {
      const data = await fetchAuthList();
      const userList = Array.isArray(data) ? data : (data?.results || []);

      const sortedData = [...userList].sort((a, b) =>
        a.username.toLowerCase().localeCompare(b.username.toLowerCase())
      );

      setUserData(sortedData);
    } catch (err) {
      console.error("Failed to fetch user list:", err);
      toast.error("Failed to fetch user list: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        let roleData = await fetchRoles();
        const roleMap = {};
        roleData.forEach(r => {
          roleMap[r.id] = r.name.toLowerCase();
        });
        setRoles(roleMap);
      } catch (err) {
        console.error("Failed to load roles:", err);
      }
    };
    loadRoles();
  }, []);

  function handleEdit(user) {
    setSelectedUser(user);
    setShowModal(true);
  }

  // Filter users based on search and role
  const filteredUsers = userData.filter((user) => {
    const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim().toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      fullName.includes(searchLower) ||
      (user.username && user.username.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower));

    let matchesRole = true;
    if (selectedRole !== "All") {
      const userRoleName = roles[user.role] || "NA";
      matchesRole = userRoleName === selectedRole;
    }

    return matchesSearch && matchesRole;
  });

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);


  return (
    <div className={`user-management-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />

      <div className="main-content">
        <Header title={t("nav.userManagement")} />
        <div className="main-divider" />

        <div className="user-page-container">
          <div className="user-list">
            <div className="page-header">
              <div className="header-left">
                <h3 className="user-list-title">{t("label.manageUsers")}</h3>
                <p className="user-list-subtitle">{t("label.manageUsersSubtitle")}</p>
              </div>
              {hasRoleFn([ROLES.ADMIN_ONLY]) && (
                <button className="add-user-btn" onClick={() => setShowModal(true)}>
                  <span className="plus-icon">+</span> {t("button.addUser")}
                </button>
              )}
            </div>

            {/* Filter controls row */}
            <div className="filter-controls-row">
              <div className="search-input-wrapper">
                <TablerSearch className="search-icon" size={15} color="var(--text-muted)" />
                <input
                  type="text"
                  className="search-input"
                  placeholder={t("placeholder.searchNameEmail")}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="filter-dropdowns-group">
                <CustomSelect
                  value={selectedRole}
                  onChange={(val) => {
                    setSelectedRole(val);
                    setCurrentPage(1);
                  }}
                  options={ROLE_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                />
              </div>
            </div>

            {loading ? (
              <LoadingSpinner message={t("loading.userDetails")} />
            ) : (
              <div className="usermanage-table-wrapper">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th style={{ width: "25%" }}>{t("label.user")}</th>
                      <th style={{ width: "25%" }}>{t("label.email")}</th>
                      <th style={{ width: "20%" }}>Organization</th>
                      <th style={{ width: "15%" }}>{t("label.role")}</th>
                      <th className="center" style={{ width: "8%" }}>{t("label.staff")}</th>
                      {hasRoleFn([ROLES.ADMIN_ONLY]) && <th className="center" style={{ width: "7%" }}>{t("label.action")}</th>}
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.map((user) => {
                      const userRoleName = roles[user.role] || "NA";
                      return (
                        <tr key={user.id}>
                          <td>
                            <div className="name-cell">
                              <div className={`avatar ${getAvatarClass(user.username)}`}>
                                {user.first_name || user.last_name
                                  ? (user.first_name[0] + (user.last_name?.[0] ?? "")).toUpperCase()
                                  : "N"}
                              </div>
                              <div className="user-identity">
                                <span className="user-fullname">
                                  {user.first_name || user.last_name
                                    ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                                    : "N/A"}
                                </span>
                                <span className="user-username">{user.username}</span>
                              </div>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                              <span className="tenant-slug-code">
                                {user.tenant_name || user.tenant || (!isBootstrapAdmin() ? getTenantId() : (user.is_bootstrap_admin ? "Public" : "—"))}
                              </span>
                              {user.domain && !user.is_bootstrap_admin && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    textTransform: "capitalize",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    backgroundColor:
                                      user.domain === "construction"
                                        ? "rgba(245, 158, 11, 0.15)"
                                        : "rgba(0, 130, 248, 0.15)",
                                    color:
                                      user.domain === "construction" ? "#D97706" : "#0082F8",
                                  }}
                                >
                                  {user.domain}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`role-badge ${userRoleName.toLowerCase() === "admin" ? "role-admin" :
                              userRoleName.toLowerCase() === "operator" ? "role-operator" : "role-na"
                              }`}>
                              {userRoleName.toLowerCase()}
                            </span>
                          </td>

                          <td className="center">
                            {user.is_staff ? (
                              <span className="staff-check-icon">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </span>
                            ) : (
                              <span className="staff-dash">—</span>
                            )}
                          </td>

                          {hasRoleFn([ROLES.ADMIN_ONLY]) && (
                            <td className="center">
                              <button className="useredit-btn" onClick={() => handleEdit(user)} title={t("button.editUser")}>
                                <img src={settingIcon} alt={t("button.edit")} className="edit-btn-icon" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {currentUsers.length === 0 && (
                      <tr>
                        <td colSpan={isBootstrapAdmin() ? 6 : (hasRoleFn([ROLES.ADMIN_ONLY]) ? 5 : 4)} className="no-users-cell">
                          {t("label.noUsersFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="table-footer">
                  <span>{t("label.showingUsers", { count: filteredUsers.length, total: userData.length })}</span>
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="users-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  {t("button.prev")}
                </button>
                <span className="pagination-text">
                  {t("label.pageOf", { current: currentPage, total: totalPages })}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  {t("button.next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <AddUserModal
          onClose={() => {
            setShowModal(false);
            setSelectedUser({});
          }}
          reloadUserList={fetchUserList}
          selectedUser={selectedUser}
        />
      )}
    </div>
  );
}

export default UserManagement;